import { createFileRoute } from "@tanstack/react-router";
import MonthlyHistoricTable from "./-monthly-historic-table";
import {
  ParamClearButton,
  ParamDatePagination,
  ParamSearchFilter,
  ParamDataTableToolbar,
  ParamPagination,
  dataTableSearchParsers,
} from "@/components/ui/param-data-table";
import {
  useGetWarehousesHistoric,
  exportWarehousesHistoric,
} from "@/requests/gen/react-query/warehouse360-analytics";
import { useGetEntitiesInfinite } from "@/requests/gen/react-query/entity";
import { useGetProductBrands } from "@/requests/gen/react-query/product-brand";
import { ProductTypeEnum } from "@/requests/gen/react-query/fastAPI.schemas";
import { keepPreviousData } from "@tanstack/react-query";
import {
  useQueryStates,
  parseAsString,
  parseAsArrayOf,
  parseAsInteger,
} from "nuqs";
import { useState } from "react";
import { useOnlyEntity } from "@/hooks/use-only-entity";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import { ExportCSV } from "@/components/ui/csv-export";

export const Route = createFileRoute(
  "/_private/_warehouse-360/_analytics/warehouse360-analytics/monthly-historic/",
)({
  component: MonthlyHistoricPage,
});

function MonthlyHistoricPage() {
  const [queryParams, setQueryParams] = useQueryStates({
    ...dataTableSearchParsers,
    date_start: parseAsString,
    date_end: parseAsString,
    product_brand_ids: parseAsArrayOf(parseAsInteger),
  });

  const onlyEntity = useOnlyEntity();
  const [fulfillerSearch, setFulfillerSearch] = useState<string | undefined>();
  const productBrands = useGetProductBrands();

  const fulfillerIds = onlyEntity ? [onlyEntity] : queryParams.entity_ids;

  const fulfillers = useGetEntitiesInfinite(
    {
      search: fulfillerSearch,
      include_ids: fulfillerIds,
      entity_types: ["Fulfiller"],
      services: ["warehouse_360"],
    },
    {
      query: {
        placeholderData: keepPreviousData,
        getNextPageParam: (lastPage, allPages) => {
          if (lastPage.items.length === 0) return undefined;
          return allPages.length + 1;
        },
      },
    },
  );

  const historicParams = {
    search: queryParams.search,
    date_start: queryParams.date_start,
    date_end: queryParams.date_end,
    entity_ids: fulfillerIds,
    product_types: queryParams.product_types,
    product_brand_ids: queryParams.product_brand_ids,
  };

  const { data, isLoading, isError } = useGetWarehousesHistoric(
    {
      ...historicParams,
      page: queryParams.page,
      limit: queryParams.limit,
    },
    { query: { placeholderData: keepPreviousData } },
  );

  return (
    <div className="pb-4">
      <title>Monthly Historic Sales - Legrande Health Portal</title>
      <ParamDataTableToolbar alwaysVisibleCount={0} overflowCount={Infinity}>
        <ParamDatePagination
          startParamName="date_start"
          endParamName="date_end"
          monthsInRange={6}
        />
        <ParamSearchFilter isFetching={false} />
        {!onlyEntity && (
          <DropdownFilter
            title="Fulfiller"
            options={[
              {
                items:
                  fulfillers.data?.pages
                    .flatMap((page) => page.items)
                    .map((fulfiller) => ({
                      label: fulfiller.name,
                      value: fulfiller.entity_id,
                    })) ?? [],
              },
            ]}
            values={queryParams.entity_ids}
            onChange={(values) => setQueryParams({ entity_ids: values })}
            search={fulfillerSearch}
            setSearch={setFulfillerSearch}
            isFetching={fulfillers.isFetchingNextPage}
            fetchNextPage={fulfillers.fetchNextPage}
          />
        )}
        <DropdownFilter
          title="Type"
          options={[
            {
              items: Object.values(ProductTypeEnum).map((productType) => ({
                label: productType,
                value: productType,
              })),
            },
          ]}
          values={queryParams.product_types}
          onChange={(values) => setQueryParams({ product_types: values })}
        />
        <DropdownFilter
          title="Brand"
          options={[
            {
              items:
                productBrands.data?.map((brand) => ({
                  label: brand.name,
                  value: brand.product_brand_id,
                })) ?? [],
            },
          ]}
          values={queryParams.product_brand_ids ?? []}
          onChange={(values) => setQueryParams({ product_brand_ids: values })}
        />
        <ParamClearButton />
      </ParamDataTableToolbar>
      <MonthlyHistoricTable
        data={data}
        isLoading={isLoading}
        isError={isError}
      />
      <div className="mt-2">
        <ParamPagination count={data?.count ?? 0}>
          <ExportCSV
            onExport={() => exportWarehousesHistoric(historicParams)}
            filename="monthly_historic_sales"
          />
        </ParamPagination>
      </div>
    </div>
  );
}
